/**
 * The Protect surfaces' text roles, and the only place one is written.
 *
 * <h3>Why this exists</h3>
 *
 * <p>"Protect fonts are ALL mismatched." Measured: 42 hand-typed {@code font:} shorthands and
 * 19 hand-typed {@code fontSize:} literals across 20 files — 15 distinct size/weight pairs that
 * appear nowhere in the token scale. Four files each kept their own identical {@code CAPTION}
 * object; five kept their own identical {@code LABEL}. Nine copies of
 * {@code 600 10.5px/16px … .08em} sat beside {@code .p7-eyebrow}, which is 11px/600/.06em.</p>
 *
 * <p>Fifteen sizes cannot be nudged into looking like one system. Every role a Protect surface
 * needs is named here once, each one a token from {@code p7-tokens.css}, and
 * {@code __tests__/typeScaleIsTheTokenScale.test.js} fails on any size decided anywhere else.</p>
 *
 * <h3>Why `fontFamily` is set as well as `font`</h3>
 *
 * <p>Every role token carries its family, so in a browser the shorthand is enough. jsdom does
 * not parse the {@code font} shorthand, so a test asserting on the family reads nothing — which
 * is the convention these files already follow, kept here rather than changed.</p>
 */

/*
 * The scale, named once.
 *
 * Written out rather than interpolated from the role name: a token assembled at runtime cannot
 * be found by grep, and `styles/__tests__/p7Tokens.test.js` — which fails on a token read but
 * never defined — could only see `--p7-text-`, which is not a token.
 */
const FONT = {
  display: 'var(--p7-text-display)',
  title: 'var(--p7-text-title)',
  section: 'var(--p7-text-section)',
  body: 'var(--p7-text-body)',
  ui: 'var(--p7-text-ui)',
  caption: 'var(--p7-text-caption)',
  h3: 'var(--p7-text-h3)',
  eyebrow: 'var(--p7-text-eyebrow)',
  metric: 'var(--p7-text-metric)',
  mono: 'var(--p7-text-mono)',
  'mono-caption': 'var(--p7-text-mono-caption)',
};

/* A role is {font + fontFamily}; a variant adds weight, colour or tracking on top. The font
   SHORTHAND resets weight, so anything that overrides it must come after. */
const role = (token) => ({
  font: FONT[token],
  fontFamily: token.startsWith('mono') ? 'var(--p7-font-mono)' : 'var(--p7-font-sans)',
});

/** The page's own name. */
export const PAGE_TITLE = { margin: 0, ...role('display'), letterSpacing: '-.01em' };

/**
 * A heading inside the page, and the question being asked.
 *
 * <p>They are the same role deliberately: in the approved design the question carries the
 * weight of a section heading because it IS the section — everything in the panel under it is
 * there to answer it.</p>
 */
export const SECTION_TITLE = { margin: 0, ...role('title'), letterSpacing: '-.01em' };

/** A panel's own label, above whatever the panel holds. Smaller than what it introduces. */
export const PANEL_TITLE = { margin: 0, ...role('section') };

/** Ordinary prose. */
export const BODY = { ...role('body'), color: 'var(--p7-ink)' };

/** A control, a row, a node's own line — the console's default. */
export const UI = { ...role('ui'), color: 'var(--p7-ink)' };

/** A quieter line under something: what it means, why it is held. */
export const MEANING = {
  display: 'block', marginTop: '2px', ...role('caption'),
  color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};

/** A note, a hint, a count — the smallest ordinary text. */
export const CAPTION = { ...role('caption'), color: 'var(--p7-ink-subtle)' };

/**
 * The paragraph under a question: what this step is for, what it will not do.
 *
 * <p>Fifteen files held a byte-identical copy of this object.</p>
 */
export const NOTE = {
  margin: 0, ...role('caption'), color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};

/**
 * The uppercase micro-label above a field or a column.
 *
 * <p>These are the numbers {@code .p7-eyebrow} already owns. Nine surfaces had typed
 * {@code 10.5px} and {@code .08em} instead, a near-miss of the thing standing next to them.</p>
 */
export const EYEBROW = {
  ...role('eyebrow'),
  letterSpacing: 'var(--p7-eyebrow-tracking)',
  textTransform: 'uppercase',
  color: 'var(--p7-ink-subtle)',
};

/** A field's own label. The scale needs no size for this: it is `ui` at 600. */
export const LABEL = { ...role('ui'), fontWeight: 600, color: 'var(--p7-ink)' };

/**
 * A numeral read as a figure rather than a heading — a count, a total, a score.
 *
 * <p>Tabular figures, so a number that changes does not move the ones beside it.</p>
 */
export const METRIC = {
  ...role('metric'), letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums',
  color: 'var(--p7-ink-strong)',
};

/** A mono pill or micro-badge: a score, a rank, a count inside a ring. */
export const MONO_BADGE = { ...role('mono-caption') };

/** Mono detail — an id, a key, a line of evidence. */
export const MONO = { ...role('mono'), color: 'var(--p7-ink-subtle)' };

/** A word standing where a figure would be: "Unrated", "Not yet scored". */
export const SUBHEAD = { ...role('h3'), color: 'var(--p7-ink)' };

/** A caption carrying weight — the name under a step in a narrow column. */
export const CAPTION_STRONG = { ...role('caption'), fontWeight: 600, color: 'var(--p7-ink)' };

/**
 * A figure in a row rather than a figure that IS the screen.
 *
 * <p>Two sizes of figure is a real distinction — a list row's score cannot be the size of the
 * dashboard's headline count — and it needs no third size: the row figure is `title`.</p>
 */
export const METRIC_ROW = {
  ...role('title'), letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums',
};

/** The unit or qualifier trailing a figure — " of 6", " ExteLet". Takes the figure's colour. */
export const SUFFIX = { ...role('section'), fontWeight: 400 };

/** A digit or a tick drawn inside a ring, where the ring's own size is the geometry. */
export const GLYPH = { ...role('eyebrow'), fontWeight: 700 };
